import API from './api'
import { Urls } from './urls'
import {enqueueSnackbar} from "../redux/actions/snackbarActions"
import store from "../redux/store";
import {successMessage} from "./alertMessages"
import snackbarMessages from "./snackbarMessages"
import GLOBALS from "./GLOBALS";
import {handleError} from "./handleError";
import { useDispatch } from "react-redux";
import { setEvaluatorStatus, setUserRoles } from "../redux/store/routeSlice";

export const PortalUsersService = {
    setInstitution: function (id, method = null, value = null) {
        let url = Urls.ApiSetInst + id;
        
        if (method === 'totp' && value === '--') {
            url += `?method=totp&value=--`;
        } else {
            const params = new URLSearchParams();
            if (method) {
                params.append('method', method);
            }
            if (value !== null && value !== undefined) {
                params.append('value', value);
            }
            const queryString = params.toString();
            if (queryString) {
                url += '?' + queryString;
            }
        }
    
        return API.post(url).then((response) => {
          // Successful institution set with permissions
          if (response?.data?.data?.permissions) {
            sessionStorage.setItem(GLOBALS.SESSION_KEYS.PERMISSIONS, JSON.stringify(response.data.data.permissions));
            sessionStorage.setItem(GLOBALS.SESSION_KEYS.IS_OWNER, JSON.stringify(response.data.data.is_owner));
            sessionStorage.setItem(GLOBALS.SESSION_KEYS.EVALUATOR_STATUS, JSON.stringify(response.data.data.evaluator_status));
            sessionStorage.setItem(GLOBALS.SESSION_KEYS.USER_ROLES, JSON.stringify(response.data.data.roles));
            sessionStorage.setItem(GLOBALS.SESSION_KEYS.TANDC_ACCEPTED, JSON.stringify(response.data.data.agree_tandc));
            // Store Apollo feature flag to control interpretation availability
            const apolloEnabled = response.data.data.apollo_enabled ?? 0;
            sessionStorage.setItem(GLOBALS.SESSION_KEYS.APOLLO_ENABLED, JSON.stringify(apolloEnabled));
            
            // Store onboarding flags if provided by backend
            if (response.data.data.onboarding_flags) {
              sessionStorage.setItem(GLOBALS.SESSION_KEYS.ONBOARDING_FLAGS, JSON.stringify(response.data.data.onboarding_flags));
            }
            
            store.dispatch(setEvaluatorStatus(JSON.stringify(response.data.data.evaluator_status)));
            store.dispatch(setUserRoles((response.data.data.roles)));
            return response.data;
          }
          
          // MFA related response
          if (response?.data?.data?.mfa_result) {
            return response.data;
          }
          
          if (response.data.data === null) {
            return null;
          }
    
          return response.data;
        })
        .catch((error) => {
          if (error?.response?.data?.data?.mfa_result) {
            return error.response.data;
          }
          if (error?.response?.data?.message === "MFA Method required") {
            return { data: { mfa_result: 'mfa_required' } };
          }
          if (error?.response) {
              // Return the whole error response so UI can potentially use it
              return Promise.reject(error.response);
          }
          handleError(error, snackbarMessages.error.portalUsers.setInstitution);
          return Promise.reject(error);
        })
    },
    listInst: function () {
      return API.post(GLOBALS.ENDPOINTS.PORTAL_USER.LIST_INST + '/0').then((response) => {
          return response.data.data
      })
          .catch((error) => {
            handleError(error, snackbarMessages.error.portalUsers.list_inst);
          })
    },
    get_inst: function (id, group_id) {
      let url = GLOBALS.ENDPOINTS.PORTAL_USER.GET_INST + '/' + id;
      if (group_id !== undefined && group_id !== null) {
        url += '?group=' + group_id;
      }
      else {
        url += '?group=0';
      }
      return API.post(url).then((response) => {
          return response.data
      })
          .catch((error) => {
            handleError(error, snackbarMessages.error.portalUsers.get_inst);
          })
    },
    get_user_roles: function () {
      return API.get(GLOBALS.ENDPOINTS.PORTAL_USER.GET_USER_ROLES + '/0').then((response) => {
          return response.data
      })
          .catch((error) => {
            handleError(error, snackbarMessages.error.portalUsers.get_user_roles);
          })
    },
    set_user_roles: function (user_id, roles, group_id) {
      // Always include group parameter, defaulting to 0 if not provided
      const groupParam = group_id !== undefined && group_id !== null ? group_id : 0;
      
      return API.post(
        GLOBALS.ENDPOINTS.PORTAL_USER.SET_USER_ROLES_INST 
        + '/' + user_id + '?roles=' + roles.join('-') + '&group=' + groupParam
      ).then((response) => {
          return response.data || []
      })
          .catch((error) => {
            handleError(error, snackbarMessages.error.portalUsers.set_user_roles);
          })
    },
    set: function (user_id, data, institution_id, role, roles, group_id=null) {
      let url = user_id ? '/' + user_id : '/0';
      if (group_id !== undefined && group_id !== null) {
        // group_id of 0 or 1 are valid values and should be included in URL
        url += '?group=' + group_id; 
      }
      const body = user_id ? {
        name: data.name,
        lastname: data.lastname,
        company: data.company,
        roles: roles.join('-'),
      } :
      {
        name: data.name,
        lastname: data.lastname,
        passwd: data.password,
        email: data.email,
        company: data.company,
        institution_id: institution_id,
        user_role: 1,
        force_enable: true,       //In the future it would be editable by front end user
        roles: roles.join('-'),
      }
     //the identifier should always be set to 0 when creating a new user
      return API.post(GLOBALS.ENDPOINTS.PORTAL_USER.SET + url, body)
        .then((response) => {
          user_id ? store.dispatch(enqueueSnackbar(successMessage(snackbarMessages.success.portalUsers.set)))
          : store.dispatch(enqueueSnackbar(successMessage(snackbarMessages.success.portalUsers.add)))
          return response.data || []
      })
          .catch((error) => {
             const message = user_id ? snackbarMessages.error.portalUsers.set : snackbarMessages.error.portalUsers.add
             handleError(error, message);
          })
    },

    // Set own user data (name, lastname, company) - for user's own profile
    setOwnUser: function (data) {
      const body = {};
      
      // Only include fields that are provided
      if (data.name !== undefined && data.name !== null) {
        body.name = data.name;
      }
      if (data.lastname !== undefined && data.lastname !== null) {
        body.lastname = data.lastname;
      }
      if (data.company !== undefined && data.company !== null) {
        body.company = data.company;
      }
      
      return API.post(GLOBALS.ENDPOINTS.PORTAL_USER.MODIFY_OWN + '/0', body)
        .then((response) => {
          return response.data || [];
        })
        .catch((error) => {
          handleError(error);
          return Promise.reject(error);
        });
    },

    // Helper method to get all unique emails from user manager onboarding
    getAllUserEmails: function(formData) {
      const allEmails = new Set();
      
      // Add user managers
      if (formData.userManagers) {
        formData.userManagers.forEach(email => allEmails.add(email));
      }
      
      // Add clinicians
      if (formData.clinicians) {
        formData.clinicians.forEach(email => allEmails.add(email));
      }
      
      // Add evaluators
      if (formData.evaluators) {
        formData.evaluators.forEach(email => allEmails.add(email));
      }
      
      return Array.from(allEmails);
    },

    // Helper method to get roles for a specific email
    getRolesForEmail: function(email, formData) {
      const roles = [];
      
      if (formData.userManagers && formData.userManagers.includes(email)) {
        roles.push('user_manager');
      }
      if (formData.clinicians && formData.clinicians.includes(email)) {
        // Always assign institutional_clinician, never clinician
        roles.push('institutional_clinician');
      }
      if (formData.evaluators && formData.evaluators.includes(email)) {
        roles.push('evaluator');
      }
      
      return roles;
    },

    // Check if user exists in the system
    checkUserExists: function(email) {
      return this.search(email)
        .then(response => {
          // If user exists, response should contain user data
          return response && response.data && response.data.length > 0;
        })
        .catch(error => {
          console.error('Error checking if user exists:', error);
          return false;
        });
    },

    // Get user ID from search result
    getUserIdFromSearch: function(email) {
      return this.search(email)
        .then(response => {
          if (response && response.data && response.data.length > 0) {
            return response.data[0].keyid; // Use keyid instead of id
          }
          return null;
        })
        .catch(error => {
          console.error('Error getting user ID from search:', error);
          return null;
        });
    },

    // Create new user with multiple roles
    createUserWithRoles: function(email, roles) {
      const institutionId = sessionStorage.getItem('institution_id');
      const userData = {
        email: email,
        name: '', // Required field
        lastname: '', // Required field
      };
      
      return this.set(
        null, // user_id = null for new user
        userData,
        institutionId,
        roles[0], // Primary role
        roles, // All roles
        0 // group_id = 0 for new users
      );
    },

    // Set user roles for existing user
    setUserRolesForUser: function(userId, roles) {
      const rolesString = roles.join('-');
      const url = `/${userId}?roles=${rolesString}&group=0`;
      
      return API.post(GLOBALS.ENDPOINTS.PORTAL_USER.SET_USER_ROLES_INST + url)
        .then((response) => {
          return response.data || [];
        })
        .catch((error) => {
          handleError(error);
          return Promise.reject(error);
        });
    },

    // Link existing user to institution and set roles
    linkUserWithRoles: function(userId, roles) {
      // First link the user to the institution
      return this.link(userId, 1, 0) // link = 1, group_id = 0
        .then(() => {
          // Then set the user roles
          return this.setUserRolesForUser(userId, roles);
        });
    },

    // Process all users from onboarding (main method)
    processOnboardingUsers: async function(formData) {
      const allEmails = this.getAllUserEmails(formData);
      const currentUserEmail = sessionStorage.getItem(GLOBALS.SESSION_KEYS.USER);
      const results = {
        created: [],
        linked: [],
        errors: [],
        adminRolesUpdated: false
      };

      // First, handle the admin user if they need role updates
      const adminRoles = this.getRolesForEmail(currentUserEmail, formData);
      if (adminRoles.length > 0) {
        try {
          
          // Always include user_manager role for admin
          const adminRolesWithManager = [...new Set([...adminRoles, 'user_manager'])];
          
          // Get current user ID from sessionStorage
          const currentUserId = sessionStorage.getItem(GLOBALS.SESSION_KEYS.USER_ID);
          if (currentUserId) {
            await this.setUserRolesForUser(currentUserId, adminRolesWithManager);
            results.adminRolesUpdated = true;
          } else {
            results.errors.push({ email: currentUserEmail, error: 'Could not get current user ID' });
          }
        } catch (error) {
          results.errors.push({ email: currentUserEmail, error: error.message });
        }
      }

      // Then process other users (excluding admin)
      for (const email of allEmails) {
        try {
          // Skip admin user - already processed above
          if (email === currentUserEmail) {
            continue;
          }

          const roles = this.getRolesForEmail(email, formData);

          // Check if user exists
          const userExists = await this.checkUserExists(email);
          
          if (userExists) {
            // User exists - link to institution and set roles
            const userId = await this.getUserIdFromSearch(email);
            
            if (userId) {
              await this.linkUserWithRoles(userId, roles);
              results.linked.push({ email, userId, roles });
            } else {
              results.errors.push({ email, error: 'Could not get user ID' });
            }
            
          } else {
            // User doesn't exist - create new user
            const createResult = await this.createUserWithRoles(email, roles);
            results.created.push({ email, roles });
          }
          
        } catch (error) {
          results.errors.push({ email, error: error.message });
        }
      }

      return results;
    },

    transfer_ownership: function (user_id) {
      const url_params= '/' + user_id;
      return API.post(GLOBALS.ENDPOINTS.PORTAL_USER.TRANSFER_OWNERSHIP + url_params )
        .then((response) => {
          store.dispatch(enqueueSnackbar(successMessage(snackbarMessages.success.portalUsers.transfer_ownership)))
          return response.data || []
      })
          .catch((error) => {
            handleError(error, snackbarMessages.error.portalUsers.transfer_ownership)
          })
    },
    search: function (email) {
      return API.post(GLOBALS.ENDPOINTS.PORTAL_USER.SEARCH + '/' + email)
        .then((response) => {
          return response.data || []
      })
          .catch((error) => {
            handleError(error, snackbarMessages.error.portalUsers.get);
          })
    },
    link: function (user_id, link, group_id) {
      const url_params= '/' + user_id + '?link='+ link + '&group=' + (group_id ?  + group_id : 0);
      return API.post(GLOBALS.ENDPOINTS.PORTAL_USER.LINK + url_params )
        .then((response) => {
          link === 1 ? store.dispatch(enqueueSnackbar(successMessage(snackbarMessages.success.portalUsers.link))) :
          store.dispatch(enqueueSnackbar(successMessage(snackbarMessages.success.portalUsers.unlink)))
          return response.data || []
      })
          .catch((error) => {
            link === 1 ? handleError(error, snackbarMessages.error.portalUsers.link)
            : handleError(error, snackbarMessages.error.portalUsers.unlink)
          })
    },
    link_user_to_instance_group_inst: function (user_id, link,group_id) {
      const url_params= '/' + user_id + '?link='+ link + '&group=' + group_id;
      return API.post(GLOBALS.ENDPOINTS.PORTAL_USER.LINK_USER_TO_INSTANCE_GROUP_INST + url_params )
        .then((response) => {
          link === 1 ? store.dispatch(enqueueSnackbar(successMessage(snackbarMessages.success.portalUsers.link))) :
          store.dispatch(enqueueSnackbar(successMessage(snackbarMessages.success.portalUsers.unlink)))
          return response.data || []
      })
          .catch((error) => {
            link === 1 ? handleError(error, snackbarMessages.error.portalUsers.link)
            : handleError(error, snackbarMessages.error.portalUsers.unlink)
          })
    },
    enableTraining: function (user_id, group_id) {
      const url_params= '/' + user_id + '?status=1&group=' + group_id;
      return API.post(GLOBALS.ENDPOINTS.PORTAL_USER.SET_EVALUATOR_STATUS_INST + url_params )
        .then((response) => {
          store.dispatch(enqueueSnackbar(successMessage(snackbarMessages.success.portalUsers.evaluator_status)))
          return response.data || []
      })
          .catch((error) => {
            handleError(error, snackbarMessages.error.portalUsers.evaluator_status)
          })
    },
    setTermsAndConditions: function () {
      return API.post(GLOBALS.ENDPOINTS.PORTAL_USER.SET_TERMS_AND_CONDITIONS_USER + '/1' )
        .then((response) => {
          store.dispatch(enqueueSnackbar(successMessage(snackbarMessages.success.portalUsers.terms_adn_conds)))
          return response.data || []
      })
          .catch((error) => {
            handleError(error, snackbarMessages.error.portalUsers.terms_adn_conds)
          })
    },

    get_own_user: function () {
      return API.get(GLOBALS.ENDPOINTS.PORTAL_USER.GET_OWN_USER + '/0').then((response) => {
          return response.data
      })
          .catch((error) => {
            handleError(error, snackbarMessages.error.portalUsers.get);
          })
    },

    setOnboarding: function (roles) {
      // Convert roles array to string format required by backend
      // e.g., ['evaluator', 'user_manager'] -> 'evaluator-user_manager'
      const rolesString = roles.join('-');
      const url = `${GLOBALS.ENDPOINTS.PORTAL_USER.SET_ONBOARDING}/0?roles=${rolesString}`;
      
      return API.post(url).then((response) => {
        return response.data;
      })
      .catch((error) => {
        handleError(error, snackbarMessages.error.portalUsers.setOnboarding);
        return Promise.reject(error);
      });
    }
};